import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config/config';
import './ReservaPistas.css';

const HoraButton = ({ hora, disponible, seleccionada, onToggle }) => (
  <button
    type="button"
    disabled={!disponible}
    className={`btn-hora ${seleccionada ? 'seleccionada' : disponible ? 'libre' : 'ocupada'}`}
    onClick={() => onToggle(hora)}
  >
    {hora.slice(0, 5)}
  </button>
);

const formatearFecha = (iso) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const HHMM = (slot) => slot.slice(0, 5);

const ReservaPistas = () => {
  /* ------------- ESTADO -------------------------------------------------- */
  const [pista, setPista]       = useState('');
  const [fecha, setFecha]       = useState('');
  const [nombre, setNombre]     = useState('');
  const [email, setEmail]       = useState('');
  const [horasSel, setHorasSel] = useState([]);
  const [horasDisp, setHorasDisp] = useState([]);
  const [msg, setMsg]           = useState('');
  const [pop, setPop]           = useState(false);
  const [mostrarInfo, setMostrarInfo] = useState(false);

  /* flags de error en inputs */
  const [nombreErr, setNombreErr] = useState(false);
  const [emailErr, setEmailErr]   = useState(false);
  const [aceptoPolitica, setAceptoPolitica] = useState(false);
  const [politicaErr, setPoliticaErr] = useState(false);

  /* ------------- DATOS ESTÁTICOS ----------------------------------------- */
  const pistas = [
    { value: 'tenis',  label: 'Tenis' },
    { value: 'padel',  label: 'Padel' },
    { value: 'futbol', label: 'Fútbol sala' }
  ];

  /* ------------- HORAS DISPONIBLES (9:00 – 21:30) ------------------------ */
  const horasTodas = React.useMemo(() => {
    const arr = [];
    for (let h = 9; h <= 21; h++) {          // ← empieza a las 9:00
      arr.push(`${String(h).padStart(2, '0')}:00:00`);
      if (h !== 21) arr.push(`${String(h).padStart(2, '0')}:30:00`);
    }
    return arr;
  }, []);

  /* ------------- FECHAS DISPONIBLES ------------------------------------- */
  const genDias = () => {
    const hoy = new Date();
    const diaSemana = hoy.getDay();   // 0=domingo … 6=sábado
    let inicio = 1;                   // por defecto, mañana

    if (diaSemana === 5) inicio = 3;  // viernes  → lunes
    else if (diaSemana === 6) inicio = 2; // sábado → lunes
    else if (diaSemana === 0) inicio = 1; // domingo → lunes

    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(hoy);
      d.setDate(hoy.getDate() + i + inicio);
      const iso = d.toISOString().split('T')[0];
      return { value: iso, label: formatearFecha(iso) };
    });
  };

  /* ------------- Al elegir pista, pre-seleccionar la primera fecha ------- */
  useEffect(() => {
    if (!pista) return;
    const dias = genDias();
    if (dias.length) setFecha(dias[0].value);
  }, [pista]);

  /* ------------- CÁLCULOS VARIOS ---------------------------------------- */
  const bloqueSize = pista === 'padel' ? 3 : 2;
  const luz = horasSel.length && horasSel[0] >= '20:00:00';

  const calcularTotal = () => {
    if (!horasSel.length) return 0;
    const bloques = horasSel.length / bloqueSize;
    if (pista === 'padel') return (luz ? 9 : 6) * bloques;
    return (luz ? 5 : 3) * bloques;
  };

  const horaInicio = horasSel[0] ? HHMM(horasSel[0]) : '';
  const horaFin = (() => {
    if (!horasSel.length) return '';
    const last = horasSel.at(-1);
    const [h, m] = last.split(':').map(Number);
    const fin = new Date(0, 0, 0, h, m + 30);
    return fin.toTimeString().slice(0, 5);
  })();

  /* ------------- Cargar horas libres ------------------------------------ */
  useEffect(() => {
    if (!pista || !fecha) { setHorasDisp([]); return; }

    fetch(`${API_BASE_URL}/reservas?pista=${pista}&dia=${fecha}`)
      .then(r => r.json())
      .then(data => {
        const ocup = data.map(r => r.hora);

        if (pista === 'padel') {
          /* bloquear los tres slots del bloque */
          const set = new Set(ocup);
          ocup.forEach(h => {
            const i = horasTodas.indexOf(h);
            if (i > -1) {
              if (i + 1 < horasTodas.length) set.add(horasTodas[i + 1]);
              if (i + 2 < horasTodas.length) set.add(horasTodas[i + 2]);
            }
          });
          horasTodas.forEach(h => { if (set.has(h) && !ocup.includes(h)) ocup.push(h); });
        }

        setHorasDisp(horasTodas.map(h => ({ hora: h, disponible: !ocup.includes(h) })));
        setHorasSel([]);
        setMsg('');
      })
      .catch(() => { setMsg('❌ No se pudieron cargar las horas.'); setHorasDisp([]); });
  }, [pista, fecha, horasTodas]);

  /* ------------- Seleccionar / des-seleccionar horas --------------------- */
  const toggleHora = (h) => {
    if (!pista) return;
    const iStart = horasTodas.indexOf(h);
    if (iStart === -1 || iStart + bloqueSize - 1 >= horasTodas.length) return;

    const grupo = horasTodas.slice(iStart, iStart + bloqueSize);

    /* quitar grupo si ya estaba entero seleccionado */
    if (grupo.every(x => horasSel.includes(x))) {
      setHorasSel(prev => prev.filter(x => !grupo.includes(x)));
      return;
    }

    /* comprobar disponibilidad */
    if (!grupo.every(x => horasDisp.find(o => o.hora === x)?.disponible)) return;

    /* comprobar contigüidad */
    const propuesta = [...horasSel, ...grupo];
    const indices = propuesta.map(s => horasTodas.indexOf(s)).sort((a, b) => a - b);
    for (let k = 1; k < indices.length; k++) {
      if (indices[k] - indices[k - 1] > 1) {
        setMsg('⚠️ Las horas deben ser contiguas. Haz otra reserva para más tramos.');
        setTimeout(() => setMsg(''), 3000);
        return;
      }
    }
    setHorasSel(indices.map(i => horasTodas[i]));
  };

  /* ------------- Validar y mostrar popup -------------------------------- */
  const reservar = () => {
    if (!pista || !fecha || !horasSel.length || !email.trim() || !nombre.trim()) {
      setMsg('⚠️ Rellena nombre, email y selecciona pista, día y horas.');
      if (!email.trim())  { setEmailErr(true);   setTimeout(() => setEmailErr(false), 3000); }
      if (!nombre.trim()) { setNombreErr(true); setTimeout(() => setNombreErr(false), 3000); }
      return;
    }
    if (!aceptoPolitica) {
      setMsg('⚠️ Debes aceptar la Política de Privacidad para continuar.');
      setPoliticaErr(true);
      setTimeout(() => setPoliticaErr(false), 3000);
      return;
    }
    setPop(true);
  };

  /* ------------- Confirmar y mandar al backend -------------------------- */
  const confirmar = () => {
    fetch(`${API_BASE_URL}/pago/crear-sesion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pista, dia: fecha, horas: horasSel, email, nombre })
    })
      .then(r => r.json())
      .then(d => d.url
        ? (window.location.href = d.url)
        : (setMsg('❌ No se pudo iniciar el pago.'), setPop(false))
      )
      .catch(() => { setMsg('❌ Error de red.'); setPop(false); });
  };

  /* ------------- RENDER -------------------------------------------------- */
  return (
    <main className="reserva-page">
      <h1 className="page-title">Reserva de pistas</h1>

      {/* Selector pista & fecha */}
      <div className="select-row">
        <div className="form-group">
          <label>Pista</label>
          <select value={pista} onChange={e => setPista(e.target.value)}>
            <option value="">—</option>
            {pistas.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Fecha</label>
          <select value={fecha} onChange={e => setFecha(e.target.value)} disabled={!pista}>
            {genDias().map(d => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Instrucciones desplegables */}
      <div className="info-reserva">
        <button onClick={() => setMostrarInfo(prev => !prev)} className="btn-info">
          {mostrarInfo ? 'Ocultar instrucciones de uso' : 'Ver instrucciones de uso'}
        </button>

        {mostrarInfo && (
          <div className="info-contenido">
            <h3>📍 Instrucciones para las reservas</h3>
            <ul>
              <li>Las llaves se recogen en el bar <strong>Los Portis</strong>, en Escalonilla.</li>
              <li>Muestra la <strong>confirmación del correo</strong> al personal del bar.</li>
              <li>Devuelve las llaves en el bar al finalizar tu reserva.</li>
              <li><strong>Pistas:</strong> C. Corporación 12, 45517 Escalonilla (Toledo)</li>
              <li><strong>Bar Los Portis:</strong> C. Molino 10, 45517 Escalonilla (Toledo)</li>
            </ul>
          </div>
        )}
      </div>

      {/* Formulario de datos personales */}
      <div className="form-group">
        <label>Nombre completo</label>
        <input
          type="text"
          value={nombre}
          onChange={e => setNombre(e.target.value)}
          className={nombreErr ? 'input-error' : ''}
        />
        {nombreErr && <span className="email-error">Introduce tu nombre completo.</span>}
      </div>

      <div className="form-group">
        <label>Email</label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className={emailErr ? 'input-error' : ''}
        />
        {emailErr && <span className="email-error">Introduce tu correo.</span>}
      </div>

      <div className={`form-group form-group-checkbox ${politicaErr ? 'checkbox-error' : ''}`}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={aceptoPolitica}
            onChange={e => setAceptoPolitica(e.target.checked)}
            className="checkbox-input"
            aria-required="true"
          />
          <span className="checkbox-text">
            He leído y acepto la{' '}
            <a
              href="/uploads/cookies/politica_de_privacidad3.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="checkbox-link"
            >
              Política de Privacidad
            </a>
          </span>
        </label>
        {politicaErr && <span className="email-error">Debes aceptar la Política de Privacidad.</span>}
      </div>

      {/* Grid de horas */}
      <div className="grid-horas">
        {horasDisp.map(({ hora, disponible }) => (
          <HoraButton
            key={hora}
            hora={hora}
            disponible={disponible}
            seleccionada={horasSel.includes(hora)}
            onToggle={toggleHora}
          />
        ))}
      </div>

      {/* Resumen horas seleccionadas */}
      {horasSel.length > 0 && (
        <p><strong>Horario seleccionado:</strong> {horaInicio} – {horaFin}</p>
      )}

      {/* Botón confirmar */}
      <button
        className="btn-confirmar principal"
        onClick={reservar}
        disabled={!pista || !fecha || !horasSel.length || !aceptoPolitica}
      >
        Confirmar
      </button>

      {/* Mensajes */}
      {msg && (
        <p className={
          msg.startsWith('❌') ? 'mensaje error'
            : msg.startsWith('⚠️') ? 'mensaje warning'
              : 'mensaje exito'
        }>
          {msg}
        </p>
      )}

      {/* Popup confirmación */}
      {pop && (
        <div className="popup-confirmacion" onClick={() => setPop(false)}>
          <div className="popup-contenido" onClick={e => e.stopPropagation()}>
            <h2>Confirmar</h2>
            <p><strong>Nombre:</strong> {nombre}</p>
            <p><strong>Pista:</strong> {pistas.find(p => p.value === pista)?.label}</p>
            <p><strong>Fecha:</strong> {formatearFecha(fecha)}</p>
            <p><strong>Horario:</strong> {horaInicio} – {horaFin}</p>
            <p><strong>Total:</strong> {calcularTotal()} €</p>
            <div className="popup-botones">
              <button onClick={confirmar} className="btn-confirmar">Pagar</button>
              <button onClick={() => setPop(false)} className="btn-cancelar">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default ReservaPistas;
